/**
 * Per-manufacturer sub-region card per scope §6.1.4. Atlas Day 2 Phase 3 +
 * Atlas Day 3a P1 cross-cell pointer wiring.
 *
 * Surface: "ISG carries products from N manufacturers; K active. Manufacturer-
 * distributor pairings gate path eligibility → Path eligibility rule above."
 *
 * Cross-cell pointer: "→ Path eligibility" wired per Atlas Day 3a P1 —
 * clickable button with stopPropagation; navigates to path-eligibility
 * inspect. Card body navigates to per-manufacturer inspect. Refactored
 * from <button> to <div role="button"> per Day 3a cross-cell pointer pattern.
 */

import type { JSX, KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Factory } from 'lucide-react';
import type { ManufacturerSubstrate } from '../../../lib/atlas/perEntitySubstrate';

export interface PerManufacturerCardProps {
  substrate: ManufacturerSubstrate;
}

export default function PerManufacturerCard({
  substrate,
}: PerManufacturerCardProps): JSX.Element {
  const navigate = useNavigate();
  const { total, active, pairingCount } = substrate;
  const pairingClause =
    pairingCount > 0
      ? `${pairingCount} manufacturer-distributor ${pairingCount === 1 ? 'pairing' : 'pairings'} gate path eligibility → Path eligibility rule above.`
      : 'No manufacturer-distributor pairings configured yet.';
  const goPrimary = (): void => {
    navigate('/admin/engine-atlas/inspect/per-manufacturer');
  };
  const onKey = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      goPrimary();
    }
  };
  return (
    <div
      role="button"
      tabIndex={0}
      data-sub-region-id="per-manufacturer"
      onClick={goPrimary}
      onKeyDown={onKey}
      className="w-full text-left border border-border-subtle bg-surface-0 rounded-md p-3 hover:border-accent transition-colors cursor-pointer"
    >
      <div className="flex items-start gap-2">
        <Factory size={16} className="text-ink-secondary flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="text-sm font-semibold text-ink-bright">Per-manufacturer</h3>
            <button
              type="button"
              data-cross-cell-pointer="path-eligibility"
              onClick={(e) => {
                e.stopPropagation();
                navigate('/admin/engine-atlas/inspect/path-eligibility');
              }}
              className="text-xs text-ink-muted hover:text-accent transition-colors cursor-pointer"
            >
              → Path eligibility
            </button>
          </div>
          <p className="text-xs text-ink-secondary mt-1">
            ISG carries products from {total} {total === 1 ? 'manufacturer' : 'manufacturers'};{' '}
            {active} active. {pairingClause}
          </p>
        </div>
      </div>
    </div>
  );
}
